import { type Editor, ReactRenderer } from '@tiptap/react'
import {
  AI_PROPOSAL_WIDGET_CLASS,
  type AIProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import {
  ChoiceSourceWidget,
  type ChoiceSourceWidgetProps,
} from '#src/components/rich-text-editor/ChoiceSourceWidget.tsx'
import { getIslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import {
  SuggestionWidget,
  type SuggestionWidgetProps,
} from '#src/components/rich-text-editor/SuggestionWidget.tsx'

export interface SuggestionWidgetShells {
  /* The DOM node the widget decoration hosts for `proposal.id`, cached across
   * decoration drop/re-add cycles. */
  getShell(proposal: AIProposal): HTMLElement
  /* Push the latest proposal content into already-mounted shells. A widget is
   * keyed by suggestion id, so PM reuses its cached DOM without re-running the
   * getShell factory when a streamed op completes/grows — the shell would stay
   * frozen at whatever content it FIRST painted (partial/empty during live
   * generation). Called after every transaction with the current proposals so
   * the read-only preview and a not-yet-opened island's seed refresh to the
   * completed content. An OPEN island is skipped: its local editor holds the
   * clinician's in-progress edit, which a prop refresh must never clobber. */
  syncProps(proposals: Map<string, AIProposal>): void
  /* Called after every transaction with the ids the plugin currently paints. */
  reconcile(liveIds: Set<string>): void
  destroyAll(): void
}

// The content a cached shell was last rendered with. A proposal's from/to remap
// to new object identities on every doc change, so dedup syncProps on the fields
// the preview actually renders — a refresh fires only when content truly grew.
// `editedContent` is part of it because the preview renders the edit in
// preference to the op: a re-paint that only picked up a newly-persisted island
// edit would otherwise dedup against the stale shell and keep showing the op.
// The citation is part of it for the same reason: a streamed op whose citation
// arrives after its content would otherwise dedup against the shell painted
// without it and never show the excerpt. So is every provenance entry, whole
// rather than its first: the excerpt renders each one, so a list that gains or
// changes an entry past the first must repaint too.
function contentSignature(p: AIProposal): string {
  return `${p.render.type}::${p.fillOnly ?? ''}::${p.htmlContent}::${p.editedContent ?? ''}::${p.label ?? ''}::${p.citation?.reference ?? ''}::${p.citation?.quote ?? ''}::${JSON.stringify(p.sourceProvenance ?? [])}`
}

/* Widget-shell cache (design §1.2; M1 de-risk: 0 empty frames over 500 cycles,
 * vs 8–11/50 for a fresh root per re-add). ProseMirror DETACHES — never
 * destroys — a widget's DOM when its decoration drops, so a shell + React root
 * cached by suggestion id and reused on re-add keeps its painted content across
 * the whole-doc-ReplaceStep drop/re-add regime that every Yjs-origin change
 * triggers (see useAISuggestionHydration.ts). A fresh root per re-add would
 * paint an empty frame each cycle — root.render is scheduled, not synchronous —
 * so only a reused shell keeps #14013's synchronous undo repaint flash-free.
 *
 * Host-owned: ReactRenderer registers each preview as a portal on the host
 * editor's contentComponent, so previews render inside the host React tree and
 * inherit its providers. An editor with no React host has no contentComponent,
 * so the mounted preview stays empty rather than wrong — the extension gates on
 * `mountPreview` upstream so no shell is even created there. */
/* Clipboard/drag boundary (design §2.a, invariant 2). The rendered widget
 * (read-only preview OR editable island) is DOM inside the host editor but NOT
 * content in its PM document, so a browser-native copy/cut/drag of it could
 * carry rich markup — a condition mention's data-type/data-reference — that a
 * paste into the shared doc would re-materialize as an UNACCEPTED mention,
 * bypassing accept, validation, and editedContent persistence. Copy/cut escape
 * as text/plain only, and the widget is not drag-out-able. A privacy guard as
 * much as a correctness one; the island's own @-mention picker remains the
 * in-island insert path. */
function makeWidgetClipboardInert(el: HTMLElement): void {
  el.draggable = false
  const plainTextOnly = (event: ClipboardEvent): void => {
    event.clipboardData?.setData(
      'text/plain',
      window.getSelection()?.toString() ?? ''
    )
    event.preventDefault()
    // Capture-phase + stopImmediatePropagation so the nested editor's own PM
    // clipboard serializer never runs to overwrite the plain-text payload with
    // rich HTML.
    event.stopImmediatePropagation()
  }
  el.addEventListener('copy', plainTextOnly, true)
  el.addEventListener('cut', plainTextOnly, true)
  el.addEventListener('dragstart', (event) => event.preventDefault(), true)
}

export function createSuggestionWidgetShells(
  editor: Editor
): SuggestionWidgetShells {
  const cache = new Map<string, ReactRenderer>()
  // Content signature last pushed to each cached shell, so syncProps re-renders a
  // preview only when its proposal content actually changed, not on every remap.
  const painted = new Map<string, string>()
  // One island registry per editor, read off the AIProposalsExtension storage;
  // passed to each widget so it can swap preview↔island and so the popover /
  // accept pipeline reach the same instance.
  const registry = getIslandRegistry(editor)
  // The authoritative live set, updated SYNCHRONOUSLY by reconcile so a
  // deferred teardown re-checks the LATEST intent — a same-task drop+re-add
  // repopulates it before the microtask runs, cancelling the teardown.
  let liveIds = new Set<string>()

  return {
    getShell(proposal) {
      const hit = cache.get(proposal.id)
      if (hit) {
        // Reuse: PM only detached this element, so its rendered content and
        // React state are intact.
        return hit.element
      }
      const options = {
        editor,
        as: 'div' as const,
        className: AI_PROPOSAL_WIDGET_CLASS,
      }
      const renderer =
        proposal.render.type === 'choiceOption'
          ? new ReactRenderer(ChoiceSourceWidget, {
              ...options,
              props: { proposal } satisfies ChoiceSourceWidgetProps,
            })
          : new ReactRenderer(SuggestionWidget, {
              ...options,
              props: {
                proposal,
                registry,
                editor,
              } satisfies SuggestionWidgetProps,
            })
      const el = renderer.element as HTMLElement
      el.contentEditable = 'false'
      /* A choice pick's shell holds only its source, and is deliberately not
       * stamped with the proposal's id: the hover popover finds a proposal by
       * that attribute, and the pick's own option button already carries it.
       * Stamped here too, the Accept/Reject toolbar would open over the source
       * line as well, on a surface with no card to act on. */
      if (proposal.render.type !== 'choiceOption') {
        // The DOM contract the hover popover / status bar read via
        // closest('[data-ai-proposal-id]').
        el.dataset.aiProposalId = proposal.id
        el.dataset.aiProposalToolCallId = proposal.toolCallId
        if (proposal.label) {
          el.dataset.aiProposalLabel = proposal.label
        }
      }
      makeWidgetClipboardInert(el)
      cache.set(proposal.id, renderer)
      painted.set(proposal.id, contentSignature(proposal))
      return el
    },

    syncProps(proposals) {
      for (const [id, renderer] of cache) {
        const proposal = proposals.get(id)
        if (!proposal) {
          continue
        }
        // Never disturb an open island — its local editor holds the clinician's
        // in-progress edit. The preview and the not-yet-opened-island seed
        // (which mounts fresh from the current prop on open) are the only paths
        // this refresh targets.
        if (registry?.isOpen(id)) {
          continue
        }
        const signature = contentSignature(proposal)
        if (painted.get(id) === signature) {
          continue
        }
        painted.set(id, signature)
        renderer.updateProps({
          proposal,
          registry,
          editor,
        } satisfies SuggestionWidgetProps)
      }
    },

    reconcile(ids) {
      liveIds = ids
      for (const [id, renderer] of cache) {
        if (!ids.has(id)) {
          // Defer teardown; only unmount if STILL absent when the microtask
          // runs (affirmative removal), so a drop+re-add in the same task never
          // tears the shell down.
          queueMicrotask(() => {
            if (!liveIds.has(id) && cache.get(id) === renderer) {
              renderer.destroy()
              cache.delete(id)
              painted.delete(id)
              /* Close the island's registry id on this AFFIRMATIVE removal.
               * Unmounting the island only unregisters its controller; the id
               * would stay in `openIds`, so a later re-add of the same proposal
               * (an undo of the accept that removed it) would auto-mount an
               * island with no click — showing the AI's original text while a
               * re-accept inserts the previously edited content, and allowing
               * two islands open at once. Deliberately NOT done when the
               * teardown is cancelled above: a drop+re-add in one task is the
               * whole-doc-replace reposition, which must keep the island open. */
              registry?.close(id)
            }
          })
        }
      }
    },

    destroyAll() {
      for (const renderer of cache.values()) {
        renderer.destroy()
      }
      cache.clear()
      painted.clear()
    },
  }
}
